/**
 * AdminHomepage — full homepage CMS panel.
 *
 * Surfaces every piece of homepage content as a directly editable list:
 *   • Marquee items   (add / edit / delete / reorder)
 *   • Hero carousel   (5+ slides — image, alt, title, subtitle, CTA)
 *   • Hero videos     (multi-video carousel — mp4 / webm / poster)
 *   • Min-order, free-delivery threshold, delivery charge
 *
 * All changes are persisted to /api/admin/settings and broadcast back to
 * the storefront via SettingsContext.refresh().
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useSettings } from "../../contexts/SettingsContext";
import { uploadMedia } from "../../lib/uploadMedia";
import { HERO_SLIDES as BUNDLED_HERO_SLIDES, CATEGORIES as BUNDLED_CATEGORIES } from "../../lib/homeContent";
import { Plus, Trash2, ArrowUp, ArrowDown, Upload, Megaphone, Layers, Film, Tags, Cloud } from "lucide-react";
import CloudinaryImagePicker from "../../components/admin/CloudinaryImagePicker";
import BestsellerCurator from "./BestsellerCurator";
import HomepageCategoryCurator from "./HomepageCategoryCurator";

const CATEGORY_NAMES = ["Pickles", "Culinary Pastes", "Spices", "Masalas", "Wood Pressed Oils", "Traditional Powders", "Flours", "Combos"];

// Bundled defaults — these mirror what's *actually currently rendered* on
// the storefront so the admin sees the live state, not a blank shell.
// Admins can edit, replace, or fully clear each section.
const DEFAULT_MARQUEE = [
  "FREE DELIVERY ON ORDERS ABOVE ₹999/-!!!",
  "MINIMUM ORDERING AMOUNT IS ₹200/-",
];
const DEFAULT_HERO_SLIDES = BUNDLED_HERO_SLIDES.map((s) => ({
  id: s.id,
  img: s.img || "",
  img_desktop: s.img_desktop || "",
  alt: s.alt || "",
  overline: s.overline || "",
  title: s.title || "",
  subtitle: s.subtitle || "",
  cta_label: s.ctaLabel || "Shop now",
  cta_href: s.ctaHref || "/shop",
}));
const DEFAULT_HERO_VIDEOS = [
  { id: "default-1", src_mp4: "https://customer-assets.emergentagent.com/job_spice-bazaar-124/artifacts/rslwgeow_download_20260617_221950_0000.mp4", src_webm: "/ad-video.webm", poster: "" },
  { id: "default-2", src_mp4: "https://customer-assets.emergentagent.com/job_spice-bazaar-124/artifacts/w6f6pusl_Sri%20Durga%20Foods%20Spices%28Advertisement%29.mp4", src_webm: "", poster: "" },
];
// Pre-fill the 7 category images with the live storefront URLs so admins
// can replace / remove them directly without first running "Restore defaults".
const DEFAULT_CATEGORY_IMAGES = Object.fromEntries(
  BUNDLED_CATEGORIES.map((c) => [c.name, c.img || ""])
);

const BLANK_SLIDE = () => ({
  id: `slide-${Date.now().toString(36)}`,
  img: "",
  img_desktop: "",
  alt: "",
  overline: "",
  title: "",
  subtitle: "",
  cta_label: "Shop now",
  cta_href: "/shop",
});

const BLANK_VIDEO = () => ({
  id: `video-${Date.now().toString(36)}`,
  src_mp4: "",
  src_webm: "",
  poster: "",
});

// Read a File via the GridFS-backed media endpoint and return the URL
// (e.g. /api/media/<id>).  Much safer for large videos and high-res
// imagery than embedding base64 dataURLs inside the settings document.
const uploadFile = async (file) => {
  if (!file) return null;
  try {
    return await uploadMedia(file);
  } catch (err) {
    throw new Error(err?.response?.data?.detail || err?.message || "Upload failed");
  }
};

export default function AdminHomepage() {
  const { refresh } = useSettings();
  const [s, setS] = useState({
    announcement_banner_text: "",
    free_delivery_threshold: 999,
    min_order_amount: 200,
    marquee_items: [],
    hero_slides: [],
    hero_videos: [],
    category_images: {},   // legacy single-image-per-category map
    bestseller_product_ids: [],
    homepage_categories: [],
  });
  const [loading, setLoading] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");
  // Live products list for the Crowd Favourites picker
  const [allProducts, setAllProducts] = useState([]);
  // iter40: Cloudinary picker state — target = {kind, index|category}
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState({ kind: "", index: -1, category: "" });
  const [pickerFolder, setPickerFolder] = useState("");

  const openHeroPicker = (i) => {
    setPickerTarget({ kind: "hero", index: i });
    setPickerFolder("Sri Durga Foods/Hero Carousel Slides");
    setPickerOpen(true);
  };
  const openHeroDesktopPicker = (i) => {
    setPickerTarget({ kind: "hero_desktop", index: i });
    setPickerFolder("Sri Durga Foods/Hero Carousel Slides(Desktop)");
    setPickerOpen(true);
  };
  const openCategoryPicker = (cat) => {
    setPickerTarget({ kind: "category", category: cat });
    setPickerFolder(`Sri Durga Foods/${cat}`);
    setPickerOpen(true);
  };
  const onPickFromCloudinary = (url) => {
    if (pickerTarget.kind === "hero") {
      const i = pickerTarget.index;
      setS((p) => ({ ...p, hero_slides: (p.hero_slides || []).map((v, idx) => idx === i ? { ...v, img: url } : v) }));
    } else if (pickerTarget.kind === "hero_desktop") {
      const i = pickerTarget.index;
      setS((p) => ({ ...p, hero_slides: (p.hero_slides || []).map((v, idx) => idx === i ? { ...v, img_desktop: url } : v) }));
    } else if (pickerTarget.kind === "category") {
      const cat = pickerTarget.category;
      setS((p) => ({ ...p, category_images: { ...(p.category_images || {}), [cat]: url || "" } }));
    } else if (pickerTarget.kind === "homepage_category") {
      const i = pickerTarget.index;
      setS((p) => ({ ...p, homepage_categories: (p.homepage_categories || []).map((v, idx) => idx === i ? { ...v, image_url: url } : v) }));
    }
  };

  useEffect(() => {
    api.get("/settings").then((r) => {
      // null sentinel from the backend means "never configured" — pre-fill
      // with bundled defaults so the admin sees something to start from.
      // An empty array, conversely, is a deliberate cleared state and we
      // honour it.
      setS({
        announcement_banner_text: r.data.announcement_banner_text || "",
        free_delivery_threshold: r.data.free_delivery_threshold || 999,
        min_order_amount: r.data.min_order_amount || 200,
        marquee_items: Array.isArray(r.data.marquee_items) ? r.data.marquee_items : [...DEFAULT_MARQUEE],
        hero_slides: Array.isArray(r.data.hero_slides) ? r.data.hero_slides : DEFAULT_HERO_SLIDES.map((s) => ({ ...s })),
        hero_videos: Array.isArray(r.data.hero_videos) ? r.data.hero_videos : DEFAULT_HERO_VIDEOS.map((v) => ({ ...v })),
        category_images: r.data.category_images && typeof r.data.category_images === "object" ? r.data.category_images : { ...DEFAULT_CATEGORY_IMAGES },
        bestseller_product_ids: Array.isArray(r.data.bestseller_product_ids) ? r.data.bestseller_product_ids : [],
        // iter42 seed + iter68 migration — if a previously-saved
        // homepage_categories list exists but doesn't yet include the
        // (newer) Combos category, append it as a disabled row so it
        // shows up in the curator without being pushed live before the
        // admin has a chance to attach an image.
        homepage_categories: (() => {
          const bundled = BUNDLED_CATEGORIES.map((c, i) => ({ name: c.name, image_url: c.img || "", unit: c.unit, enabled: c.name !== "Combos", order: i }));
          const saved = Array.isArray(r.data.homepage_categories) ? r.data.homepage_categories : [];
          if (saved.length === 0) return bundled;
          const names = new Set(saved.map((c) => c && c.name).filter(Boolean));
          const missing = bundled.filter((b) => !names.has(b.name));
          if (missing.length === 0) return saved;
          const nextOrder = Math.max(0, ...saved.map((c) => c?.order ?? 0)) + 1;
          return [
            ...saved,
            ...missing.map((m, idx) => ({ ...m, order: nextOrder + idx })),
          ];
        })(),
      });
    });
    // Load product list for Crowd Favourites picker
    api.get("/products").then((r) => setAllProducts(r.data || [])).catch(() => {});
  }, []);

  // ───── Restore defaults helpers ──────────────────────────────────────────
  const restoreMarquee = () => setS((p) => ({ ...p, marquee_items: [...DEFAULT_MARQUEE] }));
  const restoreSlides = () => setS((p) => ({ ...p, hero_slides: DEFAULT_HERO_SLIDES.map((s) => ({ ...s })) }));
  const restoreVideos = () => setS((p) => ({ ...p, hero_videos: DEFAULT_HERO_VIDEOS.map((v) => ({ ...v })) }));
  const restoreCategories = () => setS((p) => ({ ...p, category_images: { ...DEFAULT_CATEGORY_IMAGES } }));

  const save = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      await api.put("/admin/settings", s);
      await refresh();
      toast.success("Homepage content saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // ───── Marquee items helpers ─────────────────────────────────────────────
  const setMarquee = (next) => setS((p) => ({ ...p, marquee_items: next }));
  const addMarquee = () => setMarquee([...(s.marquee_items || []), ""]);
  const updateMarquee = (i, value) => setMarquee(s.marquee_items.map((v, idx) => idx === i ? value : v));
  const deleteMarquee = (i) => setMarquee(s.marquee_items.filter((_, idx) => idx !== i));
  const moveMarquee = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= s.marquee_items.length) return;
    const arr = [...s.marquee_items];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setMarquee(arr);
  };

  // ───── Hero slides helpers ───────────────────────────────────────────────
  const setSlides = (next) => setS((p) => ({ ...p, hero_slides: next }));
  const addSlide = () => setSlides([...(s.hero_slides || []), BLANK_SLIDE()]);
  const updateSlide = (i, patch) => setSlides(s.hero_slides.map((v, idx) => idx === i ? { ...v, ...patch } : v));
  const deleteSlide = (i) => setSlides(s.hero_slides.filter((_, idx) => idx !== i));
  const moveSlide = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= s.hero_slides.length) return;
    const arr = [...s.hero_slides];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setSlides(arr);
  };
  const onSlideImage = async (i, file) => {
    if (!file) return;
    setUploadingKey(`slide-${i}`);
    try {
      const url = await uploadFile(file);
      updateSlide(i, { img: url });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploadingKey("");
    }
  };

  // ───── Hero videos helpers ───────────────────────────────────────────────
  const setVideos = (next) => setS((p) => ({ ...p, hero_videos: next }));
  const addVideo = () => setVideos([...(s.hero_videos || []), BLANK_VIDEO()]);
  const updateVideo = (i, patch) => setVideos(s.hero_videos.map((v, idx) => idx === i ? { ...v, ...patch } : v));
  const deleteVideo = (i) => setVideos(s.hero_videos.filter((_, idx) => idx !== i));
  const moveVideo = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= s.hero_videos.length) return;
    const arr = [...s.hero_videos];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setVideos(arr);
  };

  const onVideoUpload = async (i, file, posterOnly = false) => {
    if (!file) return;
    setUploadingKey(`video-${i}-${posterOnly ? "poster" : "src"}`);
    try {
      const url = await uploadFile(file);
      if (posterOnly) {
        updateVideo(i, { poster: url });
      } else {
        // Detect content-type by extension; webm fields get the webm path.
        const isWebm = (file.name || "").toLowerCase().endsWith(".webm") || file.type === "video/webm";
        updateVideo(i, isWebm ? { src_webm: url } : { src_mp4: url });
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploadingKey("");
    }
  };

  // ───── Category images helpers (Pickles / Spices / … / Flours) ──────────
  const setCategoryImage = (cat, url) => {
    setS((p) => ({ ...p, category_images: { ...(p.category_images || {}), [cat]: url || "" } }));
  };
  const onCategoryImageUpload = async (cat, file) => {
    if (!file) return;
    setUploadingKey(`cat-${cat}`);
    try {
      const url = await uploadFile(file);
      setCategoryImage(cat, url);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploadingKey("");
    }
  };

  return (
    <div data-testid="admin-homepage" className="space-y-10">
      <div>
        <h1 className="font-serif text-4xl text-brand-dark mb-2">Homepage content</h1>
        <p className="text-sm text-brand-muted max-w-2xl">
          Manage the marquee, hero slides, advertisement videos and the
          delivery / ordering rules — every change goes live the instant
          you click <strong>Save changes</strong>.
        </p>
      </div>

      {/* ─── Marquee Items CRUD ─────────────────────────────────────────── */}
      <section className="bg-white border border-brand-line rounded-2xl p-6 space-y-4" data-testid="admin-marquee-section">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-brand-chili">
            <Megaphone className="w-5 h-5" strokeWidth={1.5} />
            <h2 className="font-serif text-xl text-brand-dark">Marquee items (red strip)</h2>
          </div>
          <button type="button" onClick={restoreMarquee} className="text-xs text-brand-muted hover:text-brand-chili underline" data-testid="marquee-restore-defaults">Restore defaults</button>
        </div>
        <p className="text-xs text-brand-muted">
          Each row below becomes one bullet on the scrolling red strip. Drag
          arrows reorder, the trash icon removes. Leave the list empty to
          restore the default two-item announcement.
        </p>
        <div className="space-y-2" data-testid="marquee-items-list">
          {(s.marquee_items || []).map((m, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`marquee-row-${i}`}>
              <span className="text-xs text-brand-muted w-6 tabular-nums">{i + 1}.</span>
              <input
                className="input-field flex-1"
                value={m}
                onChange={(e) => updateMarquee(i, e.target.value)}
                placeholder="FREE DELIVERY ON ORDERS ABOVE ₹999/-!!!"
                data-testid={`marquee-input-${i}`}
              />
              <button type="button" onClick={() => moveMarquee(i, -1)} disabled={i === 0} className="p-2 text-brand-muted hover:text-brand-chili disabled:opacity-30" data-testid={`marquee-up-${i}`}>
                <ArrowUp className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => moveMarquee(i, 1)} disabled={i === (s.marquee_items.length - 1)} className="p-2 text-brand-muted hover:text-brand-chili disabled:opacity-30" data-testid={`marquee-down-${i}`}>
                <ArrowDown className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => deleteMarquee(i)} className="p-2 text-brand-muted hover:text-brand-chili" data-testid={`marquee-delete-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addMarquee} className="text-xs text-brand-chili font-medium inline-flex items-center" data-testid="marquee-add-btn">
          <Plus className="w-3 h-3 mr-1" /> Add marquee item
        </button>
      </section>

      {/* ─── Delivery & Min-Order ───────────────────────────────────────── */}
      <section className="bg-white border border-brand-line rounded-2xl p-6 space-y-4" data-testid="admin-delivery-section">
        <h2 className="font-serif text-xl text-brand-dark">Delivery &amp; ordering rules</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-field">Free delivery above ₹</label>
            <input type="number" className="input-field" value={s.free_delivery_threshold}
              onChange={(e) => setS({ ...s, free_delivery_threshold: parseFloat(e.target.value) })}
              data-testid="homepage-threshold" />
          </div>
          <div>
            <label className="label-field">Minimum ordering amount ₹</label>
            <input type="number" className="input-field" value={s.min_order_amount}
              onChange={(e) => setS({ ...s, min_order_amount: parseFloat(e.target.value) })}
              data-testid="homepage-min-order" />
          </div>
        </div>
        <p className="text-xs text-brand-muted">
          Users see <em>“Add ₹X amount for Ordering”</em> until the cart subtotal hits this value, and checkout is blocked below it. Shipping cost is fetched live from Shiprocket for every order — there is no flat delivery-charge fallback.
        </p>
      </section>

      {/* ─── Hero Slides CRUD ───────────────────────────────────────────── */}
      <section className="bg-white border border-brand-line rounded-2xl p-6 space-y-4" data-testid="admin-hero-slides-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-brand-chili">
            <Layers className="w-5 h-5" strokeWidth={1.5} />
            <h2 className="font-serif text-xl text-brand-dark">Hero carousel slides</h2>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={restoreSlides} className="text-xs text-brand-muted hover:text-brand-chili underline" data-testid="slides-restore-defaults">Restore defaults</button>
            <button type="button" onClick={addSlide} className="text-xs text-brand-chili font-medium inline-flex items-center" data-testid="slide-add-btn">
              <Plus className="w-3 h-3 mr-1" /> Add slide
            </button>
          </div>
        </div>
        <p className="text-xs text-brand-muted">
          Each slide overrides the homepage carousel by its <code>id</code>.
          Leave this list empty to fall back to the bundled default 5 slides.
          Upload an image OR paste a URL. Aspect ratio shows full image without cropping.
        </p>
        <div className="space-y-3" data-testid="hero-slides-list">
          {(s.hero_slides || []).map((slide, i) => (
            <div key={slide.id} className="border border-brand-line rounded-xl p-4 space-y-3" data-testid={`slide-row-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-brand-muted uppercase tracking-wider">Slide {i + 1} · <code className="text-[10px]">{slide.id}</code></span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => moveSlide(i, -1)} disabled={i === 0} className="p-1.5 text-brand-muted hover:text-brand-chili disabled:opacity-30" data-testid={`slide-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                  <button type="button" onClick={() => moveSlide(i, 1)} disabled={i === (s.hero_slides.length - 1)} className="p-1.5 text-brand-muted hover:text-brand-chili disabled:opacity-30" data-testid={`slide-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                  <button type="button" onClick={() => deleteSlide(i)} className="p-1.5 text-brand-muted hover:text-brand-chili" data-testid={`slide-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-3 flex flex-col items-start gap-2">
                  {slide.img ? (
                    <img src={slide.img} alt="" className="w-full aspect-square object-contain bg-brand-cream border border-brand-line rounded-lg" data-testid={`slide-preview-${i}`} />
                  ) : (
                    <div className="w-full aspect-square bg-brand-cream border border-dashed border-brand-line rounded-lg flex items-center justify-center text-xs text-brand-muted">No image</div>
                  )}
                  <div className="text-[10px] uppercase tracking-wider text-brand-muted">Mobile · 12:17</div>
                  <label className="text-xs text-brand-chili font-medium cursor-pointer inline-flex items-center">
                    <Upload className="w-3 h-3 mr-1" /> Upload
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onSlideImage(i, e.target.files[0])} data-testid={`slide-upload-${i}`} />
                  </label>
                  <button
                    type="button"
                    onClick={() => openHeroPicker(i)}
                    className="text-xs text-brand-dark font-medium cursor-pointer inline-flex items-center hover:text-brand-chili"
                    data-testid={`slide-pick-cloudinary-${i}`}
                  >
                    <Cloud className="w-3 h-3 mr-1" /> Pick from Cloudinary
                  </button>

                  {/* iter41: Desktop 16:9 image — preview + picker */}
                  <div className="w-full mt-3 pt-3 border-t border-brand-line">
                    {slide.img_desktop ? (
                      <img src={slide.img_desktop} alt="" className="w-full aspect-video object-contain bg-brand-cream border border-brand-line rounded-lg" data-testid={`slide-desktop-preview-${i}`} />
                    ) : (
                      <div className="w-full aspect-video bg-brand-cream border border-dashed border-brand-line rounded-lg flex items-center justify-center text-xs text-brand-muted">No desktop image</div>
                    )}
                    <div className="text-[10px] uppercase tracking-wider text-brand-muted mt-2">Desktop · 16:9</div>
                    <button
                      type="button"
                      onClick={() => openHeroDesktopPicker(i)}
                      className="text-xs text-brand-dark font-medium cursor-pointer inline-flex items-center hover:text-brand-chili mt-1"
                      data-testid={`slide-pick-cloudinary-desktop-${i}`}
                    >
                      <Cloud className="w-3 h-3 mr-1" /> Pick desktop image
                    </button>
                  </div>
                </div>
                <div className="sm:col-span-9 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <label className="label-field">Mobile image URL · 12:17</label>
                    <input className="input-field" value={slide.img} onChange={(e) => updateSlide(i, { img: e.target.value })} placeholder="https://… or use Upload above" data-testid={`slide-img-${i}`} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-field">Desktop image URL · 16:9</label>
                    <input className="input-field" value={slide.img_desktop || ""} onChange={(e) => updateSlide(i, { img_desktop: e.target.value })} placeholder="https://res.cloudinary.com/… (1672×941)" data-testid={`slide-img-desktop-${i}`} />
                  </div>
                  <div>
                    <label className="label-field">ID (used to override default slide)</label>
                    <input className="input-field" value={slide.id} onChange={(e) => updateSlide(i, { id: e.target.value })} data-testid={`slide-id-${i}`} />
                  </div>
                  <div>
                    <label className="label-field">Alt text</label>
                    <input className="input-field" value={slide.alt} onChange={(e) => updateSlide(i, { alt: e.target.value })} data-testid={`slide-alt-${i}`} />
                  </div>
                  <div>
                    <label className="label-field">Overline</label>
                    <input className="input-field" value={slide.overline} onChange={(e) => updateSlide(i, { overline: e.target.value })} data-testid={`slide-overline-${i}`} />
                  </div>
                  <div>
                    <label className="label-field">Title</label>
                    <input className="input-field" value={slide.title} onChange={(e) => updateSlide(i, { title: e.target.value })} data-testid={`slide-title-${i}`} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label-field">Subtitle</label>
                    <input className="input-field" value={slide.subtitle} onChange={(e) => updateSlide(i, { subtitle: e.target.value })} data-testid={`slide-subtitle-${i}`} />
                  </div>
                  <div>
                    <label className="label-field">CTA label</label>
                    <input className="input-field" value={slide.cta_label} onChange={(e) => updateSlide(i, { cta_label: e.target.value })} data-testid={`slide-cta-label-${i}`} />
                  </div>
                  <div>
                    <label className="label-field">CTA href</label>
                    <input className="input-field" value={slide.cta_href} onChange={(e) => updateSlide(i, { cta_href: e.target.value })} data-testid={`slide-cta-href-${i}`} />
                  </div>
                </div>
              </div>
            </div>
          ))}
          {(s.hero_slides || []).length === 0 && (
            <p className="text-xs text-brand-muted italic">No overrides set — the 5 default hero slides are showing on the homepage. Click <strong>Add slide</strong> above to start customising.</p>
          )}
        </div>
      </section>

      {/* ─── Hero Videos CRUD ───────────────────────────────────────────── */}
      <section className="bg-white border border-brand-line rounded-2xl p-6 space-y-4" data-testid="admin-hero-videos-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-brand-chili">
            <Film className="w-5 h-5" strokeWidth={1.5} />
            <h2 className="font-serif text-xl text-brand-dark">Advertisement videos</h2>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={restoreVideos} className="text-xs text-brand-muted hover:text-brand-chili underline" data-testid="videos-restore-defaults">Restore defaults</button>
            <button type="button" onClick={addVideo} className="text-xs text-brand-chili font-medium inline-flex items-center" data-testid="video-add-btn">
              <Plus className="w-3 h-3 mr-1" /> Add video
            </button>
          </div>
        </div>
        <p className="text-xs text-brand-muted">
          Customers cycle through every video one after another. MP4 source is required;
          a WebM source improves load speed but is optional. Use the arrows to reorder.
          For large videos, paste the hosted URL (Cloudinary / S3) instead of uploading.
        </p>
        <div className="space-y-3" data-testid="hero-videos-list">
          {(s.hero_videos || []).map((vid, i) => (
            <div key={vid.id} className="border border-brand-line rounded-xl p-4 space-y-3" data-testid={`video-row-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-brand-muted uppercase tracking-wider">Video {i + 1} · <code className="text-[10px]">{vid.id}</code></span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => moveVideo(i, -1)} disabled={i === 0} className="p-1.5 text-brand-muted hover:text-brand-chili disabled:opacity-30" data-testid={`video-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                  <button type="button" onClick={() => moveVideo(i, 1)} disabled={i === (s.hero_videos.length - 1)} className="p-1.5 text-brand-muted hover:text-brand-chili disabled:opacity-30" data-testid={`video-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                  <button type="button" onClick={() => deleteVideo(i)} className="p-1.5 text-brand-muted hover:text-brand-chili" data-testid={`video-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-3">
                  <label className="label-field">MP4 video (upload required)</label>
                  <div className="flex items-center gap-3 flex-wrap">
                    {vid.src_mp4 && (
                      <video src={vid.src_mp4} className="w-32 h-20 bg-black rounded border border-brand-line object-contain" muted />
                    )}
                    <label className="btn-secondary cursor-pointer inline-flex">
                      <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      {uploadingKey === `video-${i}-src` ? "Uploading…" : vid.src_mp4 ? "Replace MP4" : "Upload MP4"}
                      <input type="file" accept="video/mp4,video/quicktime,video/*" className="hidden" onChange={(e) => onVideoUpload(i, e.target.files[0])} data-testid={`video-mp4-upload-${i}`} />
                    </label>
                    {vid.src_mp4 && <button type="button" onClick={() => updateVideo(i, { src_mp4: "" })} className="text-brand-chili text-xs hover:underline" data-testid={`video-mp4-remove-${i}`}>Remove</button>}
                    <input className="input-field flex-1 min-w-[200px]" value={vid.src_mp4} onChange={(e) => updateVideo(i, { src_mp4: e.target.value })} placeholder="or paste hosted URL" data-testid={`video-mp4-${i}`} />
                  </div>
                </div>
                <div>
                  <label className="label-field">WebM (optional, smaller)</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs text-brand-chili font-medium cursor-pointer inline-flex items-center">
                      <Upload className="w-3 h-3 mr-1" /> Upload
                      <input type="file" accept="video/webm" className="hidden" onChange={(e) => onVideoUpload(i, e.target.files[0])} data-testid={`video-webm-upload-${i}`} />
                    </label>
                    {vid.src_webm && <button type="button" onClick={() => updateVideo(i, { src_webm: "" })} className="text-brand-chili text-xs hover:underline" data-testid={`video-webm-remove-${i}`}>Clear</button>}
                  </div>
                  <input className="input-field mt-1" value={vid.src_webm} onChange={(e) => updateVideo(i, { src_webm: e.target.value })} placeholder="/api/media/… or URL" data-testid={`video-webm-${i}`} />
                </div>
                <div>
                  <label className="label-field">Poster image (optional)</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs text-brand-chili font-medium cursor-pointer inline-flex items-center">
                      <Upload className="w-3 h-3 mr-1" /> Upload
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => onVideoUpload(i, e.target.files[0], true)} data-testid={`video-poster-upload-${i}`} />
                    </label>
                    {vid.poster && <button type="button" onClick={() => updateVideo(i, { poster: "" })} className="text-brand-chili text-xs hover:underline" data-testid={`video-poster-remove-${i}`}>Clear</button>}
                  </div>
                  <input className="input-field mt-1" value={vid.poster} onChange={(e) => updateVideo(i, { poster: e.target.value })} placeholder="/api/media/… or URL" data-testid={`video-poster-${i}`} />
                </div>
              </div>
            </div>
          ))}
          {(s.hero_videos || []).length === 0 && (
            <p className="text-xs text-brand-muted italic">No videos configured — the bundled default advertisement is playing. Click <strong>Add video</strong> above to start managing your own.</p>
          )}
        </div>
      </section>

      {/* ─── Shop By Category — image overrides for all 7 categories ──── */}
      <section className="bg-white border border-brand-line rounded-2xl p-6 space-y-4" data-testid="admin-category-images-section">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-brand-chili">
            <Tags className="w-5 h-5" strokeWidth={1.5} />
            <h2 className="font-serif text-xl text-brand-dark">Shop by Category — images</h2>
          </div>
          <button type="button" onClick={restoreCategories} className="text-xs text-brand-muted hover:text-brand-chili underline" data-testid="categories-restore-defaults">Restore defaults</button>
        </div>
        <p className="text-xs text-brand-muted">
          Replace any of the 7 category card images shown in <em>Shop By Category</em> on the homepage.
          Direct upload from your device. Leave a row blank to fall back to the bundled default.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="category-images-list">
          {CATEGORY_NAMES.map((cat) => {
            const url = s.category_images?.[cat] || "";
            return (
              <div key={cat} className="border border-brand-line rounded-xl p-4 flex items-center gap-4" data-testid={`category-row-${cat}`}>
                {url ? (
                  <div className="relative">
                    <img src={url} alt={cat} className="w-20 h-20 object-cover rounded-lg bg-brand-cream border border-brand-line" data-testid={`category-image-${cat}`} />
                    <button type="button" onClick={() => setCategoryImage(cat, "")} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-brand-chili text-white flex items-center justify-center shadow" aria-label={`Remove ${cat} image`} data-testid={`category-remove-${cat}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-lg border border-dashed border-brand-line bg-brand-cream flex items-center justify-center text-[10px] text-brand-muted text-center px-1">Default</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-brand-dark mb-2">{cat}</div>
                  <label className="text-xs text-brand-chili font-medium cursor-pointer inline-flex items-center" data-testid={`category-upload-label-${cat}`}>
                    <Upload className="w-3 h-3 mr-1" />
                    {uploadingKey === `cat-${cat}` ? "Uploading…" : url ? "Replace image" : "Upload image"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => onCategoryImageUpload(cat, e.target.files[0])} data-testid={`category-upload-${cat}`} />
                  </label>
                  <button
                    type="button"
                    onClick={() => openCategoryPicker(cat)}
                    className="text-xs text-brand-dark font-medium cursor-pointer inline-flex items-center hover:text-brand-chili ml-3"
                    data-testid={`category-pick-cloudinary-${cat}`}
                  >
                    <Cloud className="w-3 h-3 mr-1" /> Pick from Cloudinary
                  </button>
                  <input className="input-field text-xs mt-2" placeholder="or paste URL" value={url} onChange={(e) => setCategoryImage(cat, e.target.value)} data-testid={`category-url-${cat}`} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ───────────────────── iter42 — Crowd Favourites ─────────────────── */}
      <BestsellerCurator
        productIds={s.bestseller_product_ids || []}
        allProducts={allProducts}
        onChange={(ids) => setS((p) => ({ ...p, bestseller_product_ids: ids }))}
      />

      {/* ──────────────── iter42 — Shop By Category (curated) ────────────── */}
      <HomepageCategoryCurator
        items={s.homepage_categories || []}
        onChange={(items) => setS((p) => ({ ...p, homepage_categories: items }))}
        openPicker={(idx) => {
          setPickerTarget({ kind: "homepage_category", index: idx });
          const cat = (s.homepage_categories || [])[idx];
          setPickerFolder(cat?.name ? `Sri Durga Foods/${cat.name}` : "Sri Durga Foods");
          setPickerOpen(true);
        }}
      />

      {/* ─── Save bar ───────────────────────────────────────────────────── */}
      <div className="sticky bottom-4 z-10">
        <button type="button" onClick={save} disabled={loading} className="btn-primary w-full sm:w-auto" data-testid="homepage-save-btn">
          {loading ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* iter40 — Cloudinary image picker (admin-only) */}
      <CloudinaryImagePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={onPickFromCloudinary}
        defaultFolder={pickerFolder}
      />
    </div>
  );
}
